import { Injectable, TemplateRef } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { PhSnackBarComponent } from '../components/ph-snack-bar/ph-snack-bar.component';
import { NavigationEnd, Router, Event } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface ISnackBarConfig {
	/** notification display time in seconds */
	durationSeconds?: number;
	/** will there be an OK button that closes the notification */
	closeButton?: boolean;
	/** an array of buttons that do some action when closed */
	closeActions?: Array<ISnackBarAction>;
	/** the text to be displayed */
	messageText: string;
	/** the second text to be displayed */
	secondText?: string;
	/** parameters for translation */
	translateParams?: Object;
	/** extended info template */
	content?: TemplateRef<any>;
	/** extended info context */
	contentContext?: any;
}

export interface ISnackBarAction {
	action: string;
	callback: () => void;
}

const DEFAULT_CONFIG = {
	closeButton: true,
	closeAction: null,
	durationSeconds: 3
};

@Injectable({
	providedIn: 'root'
})
export class SnackBarService {
	private _isForbiddenErrorActive: boolean = false;
	private _snackBarRef: MatSnackBarRef<PhSnackBarComponent>;

	constructor(
		private snackBar: MatSnackBar,
		private router: Router
	) {}

	setErrorNotification(): void {
		if (this._isForbiddenErrorActive) return;

		this.setSnackBarNotification({
			messageText: 'ERROR.INFO',
			secondText: 'ERROR.TRY_AGAIN',
			durationSeconds: 2 * 60 * 60,
			closeActions: [
				{
					action: 'ERROR.TO_SUPPORT',
					callback: () => window.$crisp.push(['do', 'chat:open'])
				}
			]
		});
	}

	setForbiddenErrorNotification(): void {
		this._isForbiddenErrorActive = true;

		this.setSnackBarNotification({
			messageText: 'NO_ACCESS_MSG'
		});
	}

	setSnackBarNotification(config: ISnackBarConfig): void {
		this._snackBarRef = this.snackBar.openFromComponent(PhSnackBarComponent, {
			duration: (config.durationSeconds ?? DEFAULT_CONFIG.durationSeconds) * 1000,
			horizontalPosition: 'right',
			verticalPosition: 'top',
			panelClass: 'snack-bar-container',
			data: {
				closeButton: config.closeButton ?? DEFAULT_CONFIG.closeButton,
				closeActions: config.closeActions ?? DEFAULT_CONFIG.closeAction,
				messageText: config.messageText,
				secondText: config.secondText,
				translateParams: config.translateParams,
				content: config.content,
				contentContext: config.contentContext
			}
		});
		const routerSubscription: Subscription = this.router.events
			.pipe(filter((event: Event) => event instanceof NavigationEnd))
			.subscribe(() => this._snackBarRef.dismiss());

		const dismissSubscription: Subscription = this._snackBarRef.afterDismissed().subscribe(() => {
			this._isForbiddenErrorActive = false;
			routerSubscription.unsubscribe();
			dismissSubscription.unsubscribe();
		});
	}
}
